import React, { useState, useEffect } from 'react'
import { Route, Routes, useLocation, Navigate, NavLink } from 'react-router-dom'
import styled from 'styled-components'
import Tokens from '../../pages/Tokens'
import QueueExecutions from '../../pages/QueueExecutions'
import Settings from '../../pages/Settings'
import Calendar from '../../pages/Calendar'
import User from '../../pages/User'
import Notifications from '../../pages/Notifications'
import EnvironmentLogo from '../../assets/sidebar/Environment'
import TokensLogo from '../../assets/sidebar/Tokens'
import ConfigLogo from '../../assets/sidebar/Config'
import LogoutIcon from '../../assets/sidebar/Logout'
import UserLogo from '../../assets/sidebar/User'
import EnvironmentsList from '../../pages/EnvironmentsList'
import Environment from '../../components/environment/Environment'
import QueueLogo from '../../assets/sidebar/Queue'
import CalendarLogo from '../../assets/sidebar/Calendar'
import Logs from '../settings/logs/Logs'
import LogsGroup from '../settings/logs/LogsGroup'
import LogsItem from '../settings/logs/LogsItem'
import Registry from '../settings/Registry'
import Users from '../settings/UserList'
import Networks from '../settings/network/NetworksPage'
import Rpcs from '../settings/network/Rpcs'
import Crons from '../settings/Crons'
import Pods from '../settings/Pods'
import Subgraphs from '../settings/Subgraphs'
import EnvironmentConfig from '../../components/environmentConfig/EnvironmentConfig'
import Config from '../settings/Config'
import StagingBanner from './StagingBanner'
import { useAuth } from '../../context/AuthContext'
import useAdminNotifications from '../../hooks/useAdminNotifications'
import { useBookmarks } from '../../context/BookmarksContext'

const LOGO_URL = 'https://protocol.mimic.fi/assets/logo.svg'
const SIDEBAR_W = 200

function BellIcon({ fill = '#777' }: { fill?: string }) {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" stroke={fill} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M13.73 21a2 2 0 0 1-3.46 0" stroke={fill} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

const NAV_ITEMS = [
  { to: '/dashboard/environments', name: '/environments', label: 'Environments', Icon: EnvironmentLogo },
  { to: '/dashboard/tokens',       name: '/tokens',       label: 'Tokens',       Icon: TokensLogo },
  { to: '/dashboard/queue-executions', name: '/queue-executions', label: 'Queue', Icon: QueueLogo },
  { to: '/dashboard/calendar',     name: '/calendar',     label: 'Calendar',     Icon: CalendarLogo },
  { to: '/dashboard/settings',     name: '/settings',     label: 'Settings',     Icon: ConfigLogo },
]

export default function Dashboard() {
  const location = useLocation()
  const { logout } = useAuth()
  const [logoutHovered, setLogoutHovered] = useState(false)
  const [bookmarksOpen, setBookmarksOpen] = useState(true)
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const { areNewNotifications } = useAdminNotifications()
  const { bookmarks } = useBookmarks()

  useEffect(() => {
    setSidebarOpen(false)
  }, [location.pathname])

  return (
    <Shell>
      <StagingBanner />
      <MobileBar>
        <HamburgerBtn onClick={() => setSidebarOpen((v) => !v)} aria-label="Toggle menu">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            {sidebarOpen
              ? <><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></>
              : <><line x1="3" y1="6" x2="21" y2="6" /><line x1="3" y1="12" x2="21" y2="12" /><line x1="3" y1="18" x2="21" y2="18" /></>
            }
          </svg>
        </HamburgerBtn>
        <MobileLogo src={LOGO_URL} alt="Mimic" />
        <div style={{ width: 36 }} />
      </MobileBar>

      <Layout>
        <Overlay $visible={sidebarOpen} onClick={() => setSidebarOpen(false)} />

        <Sidebar $open={sidebarOpen}>
          <SidebarTop>
            <LogoRow>
              <SidebarLogo src={LOGO_URL} alt="Mimic" />
            </LogoRow>

            <Nav>
              {NAV_ITEMS.map(({ to, name, label, Icon }) => {
                const active = location.pathname.includes(name)
                const isEnv = name === '/environments'
                return (
                  <React.Fragment key={to}>
                    <SideNavLink to={to} $active={active}>
                      <IconWrap><Icon fill={active ? '#a89aff' : '#777'} /></IconWrap>
                      <NavLabel $active={active}>{label}</NavLabel>
                      {isEnv && bookmarks.length > 0 && (
                        <CollapseBtn
                          onClick={(e) => { e.preventDefault(); e.stopPropagation(); setBookmarksOpen((v) => !v) }}
                          title={bookmarksOpen ? 'Collapse' : 'Expand'}
                        >
                          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            {bookmarksOpen
                              ? <polyline points="18 15 12 9 6 15" />
                              : <polyline points="6 9 12 15 18 9" />}
                          </svg>
                        </CollapseBtn>
                      )}
                    </SideNavLink>
                    {isEnv && bookmarksOpen && bookmarks.map((b) => {
                      const bActive = location.pathname.includes(b.mimicId)
                      return (
                        <BookmarkItem key={b.mimicId} to={`/dashboard/environments/${b.mimicId}/`} $active={bActive}>
                          <BookmarkDot $active={bActive} />
                          <BookmarkName $active={bActive}>{b.namespace.replace(/-/g, ' ')}</BookmarkName>
                        </BookmarkItem>
                      )
                    })}
                  </React.Fragment>
                )
              })}
            </Nav>
          </SidebarTop>

          <SidebarBottom>
            <SideNavLink to="/dashboard/notifications" $active={location.pathname.includes('/notifications')}>
              <IconWrap>
                <BellIcon fill={location.pathname.includes('/notifications') ? '#a89aff' : '#777'} />
                {areNewNotifications && <NotifDot />}
              </IconWrap>
              <NavLabel $active={location.pathname.includes('/notifications')}>Notifications</NavLabel>
            </SideNavLink>

            <SideNavLink to="/dashboard/user" $active={location.pathname.includes('/user')}>
              <IconWrap><UserLogo fill={location.pathname.includes('/user') ? '#a89aff' : '#777'} /></IconWrap>
              <NavLabel $active={location.pathname.includes('/user')}>User</NavLabel>
            </SideNavLink>

            <LogoutRow
              onMouseEnter={() => setLogoutHovered(true)}
              onMouseLeave={() => setLogoutHovered(false)}
              onClick={logout}
            >
              <IconWrap><LogoutIcon fill={logoutHovered ? '#a89aff' : '#777'} /></IconWrap>
              <NavLabel $active={logoutHovered}>Logout</NavLabel>
            </LogoutRow>
          </SidebarBottom>
        </Sidebar>

        <Main>
          <div className="dashboard-content">
            <Routes>
              <Route path="/settings" element={<Settings />}>
                <Route path="registry/*" element={<Registry />} />
                <Route path="config/*" element={<Config />} />
                <Route path="users/*" element={<Users />} />
                <Route path="networks/*" element={<Networks />} />
                <Route path="rpcs/*" element={<Rpcs />} />
                <Route path="subgraphs/*" element={<Subgraphs />} />
                <Route path="crons/*" element={<Crons />} />
                <Route path="pods/*" element={<Pods />} />
                <Route path="logs/*" element={<Logs />}>
                  <Route path="groups/*" element={<LogsGroup />} />
                  <Route path=":id/groups/*" element={<LogsItem />} />
                </Route>
              </Route>
              <Route path="/settings/:id/*" element={<Settings />} />
              <Route path="/queue-executions" element={<QueueExecutions />} />
              <Route path="/tokens" element={<Tokens />} />
              <Route path="/tokens/:tab" element={<Tokens />} />
              <Route path="/environments" element={<EnvironmentsList />} />
              <Route path="/environments/:id" element={<Environment />} />
              <Route path="/environments/config/:id" element={<EnvironmentConfig />} />
              <Route path="/environments/:id/:tab" element={<Environment />} />
              <Route path="/calendar" element={<Calendar />} />
              <Route path="/user" element={<User />} />
              <Route path="/notifications" element={<Notifications />} />
              <Route path="/" element={<Navigate to="/dashboard/environments" replace />} />
            </Routes>
          </div>
        </Main>
      </Layout>
    </Shell>
  )
}

// ─── Styles ──────────────────────────────────────────────────────────────────

const Shell = styled.div`
  height: 100vh;
  background: #0a0a0a;
  display: flex;
  flex-direction: column;
`

const Layout = styled.div`
  flex: 1;
  display: flex;
  overflow: hidden;
`

const MobileBar = styled.div`
  display: none;

  @media (max-width: 768px) {
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: 52px;
    padding: 0 16px;
    background: #0a0a0a;
    border-bottom: 1px solid rgba(255, 255, 255, 0.06);
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    z-index: 40;
    flex-shrink: 0;
  }
`

const MobileLogo = styled.img`
  height: 18px;
  width: auto;
`

const HamburgerBtn = styled.button`
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  color: #a0a0a0;
  cursor: pointer;
  border-radius: 6px;
  padding: 0;
  transition: background 0.15s, color 0.15s;

  &:hover {
    background: rgba(255, 255, 255, 0.06);
    color: #ededed;
  }
`

const Overlay = styled.div<{ $visible: boolean }>`
  display: none;

  @media (max-width: 768px) {
    display: block;
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.6);
    z-index: 45;
    opacity: ${({ $visible }) => $visible ? 1 : 0};
    pointer-events: ${({ $visible }) => $visible ? 'auto' : 'none'};
    transition: opacity 0.25s ease;
  }
`

const Sidebar = styled.aside<{ $open: boolean }>`
  width: ${SIDEBAR_W}px;
  flex-shrink: 0;
  background: #0a0a0a;
  border-right: 1px solid rgba(255, 255, 255, 0.06);
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 16px 8px;
  position: fixed;
  top: 0;
  left: 0;
  height: 100vh;
  box-sizing: border-box;
  z-index: 50;

  @media (max-width: 768px) {
    transform: translateX(${({ $open }) => $open ? '0' : '-100%'});
    transition: transform 0.25s ease;
    box-shadow: ${({ $open }) => $open ? '4px 0 32px rgba(0,0,0,0.7)' : 'none'};
  }
`

const SidebarTop = styled.div`display: flex; flex-direction: column; gap: 20px;`
const SidebarBottom = styled.div`display: flex; flex-direction: column; gap: 2px;`

const LogoRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 4px 8px 12px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
`

const SidebarLogo = styled.img`height: 20px; width: auto;`

const LogoName = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: #ededed;
  letter-spacing: -0.01em;
`

const Nav = styled.nav`display: flex; flex-direction: column; gap: 2px;`

const SideNavLink = styled(NavLink)<{ $active: boolean }>`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 7px 8px;
  border-radius: 6px;
  text-decoration: none;
  transition: background 0.15s;
  background: ${({ $active }) => $active ? 'rgba(111, 92, 230, 0.12)' : 'transparent'};

  &:hover {
    background: ${({ $active }) => $active ? 'rgba(111, 92, 230, 0.15)' : 'rgba(255, 255, 255, 0.04)'};
  }
`

const IconWrap = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  svg { width: 16px; height: 16px; }
`

const NavLabel = styled.span<{ $active?: boolean }>`
  font-size: 13px;
  font-weight: 500;
  color: ${({ $active }) => $active ? '#a89aff' : '#777'};
  white-space: nowrap;
  transition: color 0.15s;
`

const CollapseBtn = styled.div`
  margin-left: auto;
  display: flex;
  align-items: center;
  color: #555;
  padding: 2px;
  border-radius: 3px;
  transition: color 0.15s;
  &:hover { color: #a0a0a0; }
`

const BookmarkItem = styled(NavLink)<{ $active: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 5px 8px 5px 28px;
  border-radius: 5px;
  text-decoration: none;
  transition: background 0.15s;
  background: ${({ $active }) => $active ? 'rgba(111,92,230,0.1)' : 'transparent'};
  &:hover { background: rgba(255,255,255,0.04); }
`

const BookmarkDot = styled.div<{ $active: boolean }>`
  width: 5px;
  height: 5px;
  border-radius: 50%;
  flex-shrink: 0;
  background: ${({ $active }) => $active ? '#a89aff' : '#444'};
`

const BookmarkName = styled.span<{ $active: boolean }>`
  font-size: 12px;
  color: ${({ $active }) => $active ? '#a89aff' : '#777'};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  text-transform: capitalize;
  transition: color 0.15s;
`

const NotifDot = styled.div`
  position: absolute;
  top: -2px;
  right: -2px;
  width: 5px;
  height: 5px;
  background: #6f5ce6;
  border-radius: 50%;
  border: 1.5px solid #0a0a0a;
`

const LogoutRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 7px 8px;
  border-radius: 6px;
  cursor: pointer;
  transition: background 0.15s;
  &:hover { background: rgba(255, 255, 255, 0.04); }
`

const Main = styled.main`
  margin-left: ${SIDEBAR_W}px;
  width: calc(100vw - ${SIDEBAR_W}px);
  min-height: 100vh;
  overflow-y: auto;
  background: #0a0a0a;

  @media (max-width: 768px) {
    margin-left: 0;
    width: 100%;
    padding-top: 52px;
  }

  .dashboard-content {
    width: 100%;
    padding: 28px;
    box-sizing: border-box;

    @media (max-width: 768px) {
      padding: 16px;
    }
  }

  h2 {
    font-size: 24px;
    font-weight: 600;
    margin-top: 40px;
    margin-bottom: 16px;
    color: #ededed;
    letter-spacing: -0.02em;
  }

  .green { color: #33c2b0; }
  .yellow { color: #ffcc33; }
  .orange { color: #ff8927; }
  .red { color: #de0000; }
  .pointer { cursor: pointer; }
`
